import { gql, useApolloClient } from '@apollo/client'
import HistoryIcon from '@mui/icons-material/History'
import ErrorIcon from '@mui/icons-material/Report'
import { Box, Button, Typography } from '@mui/material'
import { useEffect } from 'react'
import { type ErrorProps, Title, useTranslate } from 'react-admin'

/**
 * What a page shows when it breaks ("Etwas ist schief gelaufen"): react-admin's
 * page, plus the error's message in small print — so a screenshot says what
 * broke — and a record of it in `client_errors` (page, error, stack, bundle),
 * so it can be looked into without one.
 */
const REPORT = gql`
  mutation ReportClientError($error: client_errors_insert_input!) {
    insert_client_errors_one(object: $error) {
      id
    }
  }
`

/** Reported in this page's lifetime: a re-render does not record it again. */
const reported = new Set<string>()

/** The bundle this page runs (assets/index-<hash>.js): which source map reads a stack. */
const release = () =>
  document
    .querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
    ?.src.split('/')
    .pop() ?? null

export const ErrorPage = ({ error, errorInfo, title }: ErrorProps) => {
  const translate = useTranslate()
  const apollo = useApolloClient()
  const message = String(error?.message ?? error)

  useEffect(() => {
    const { pathname, search, hash } = window.location
    const url = pathname + search + hash
    const key = `${url}\n${message}`
    if (reported.has(key)) return
    reported.add(key)
    apollo
      .mutate({
        mutation: REPORT,
        variables: {
          error: {
            url,
            message: message.slice(0, 2000),
            stack: error?.stack?.slice(0, 8000) ?? null,
            component_stack: errorInfo?.componentStack?.slice(0, 8000) ?? null,
            user_agent: navigator.userAgent,
            release: release(),
          },
        },
      })
      .catch(() => {})
  }, [apollo, error, errorInfo, message])

  return (
    <>
      {title && <Title title={title} />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: 2,
          gap: 2,
          color: 'text.secondary',
        }}
      >
        <Typography
          variant='h4'
          component='h1'
          role='alert'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ErrorIcon fontSize='large' />
          {translate('ra.page.error')}
        </Typography>
        <Typography>{translate('ra.message.error')}</Typography>
        <Typography
          variant='caption'
          data-testid='error-details'
          sx={{ fontFamily: 'monospace', maxWidth: 800, overflowWrap: 'anywhere' }}
        >
          {translate('error_page.details')}: {message}
        </Typography>
        <Button
          variant='contained'
          startIcon={<HistoryIcon />}
          onClick={() => window.history.go(-1)}
        >
          {translate('ra.action.back')}
        </Button>
      </Box>
    </>
  )
}

export default ErrorPage
